import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatMinorAmount } from "@/constants/billingCatalog";

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

function previewCopy(preview, offer, currentLegacyTier) {
  if (preview.settlement === "replace") {
    return {
      body: `You'll be charged ${formatMinorAmount(offer.amount_minor, offer.currency)} starting today. Your current subscription will be canceled immediately. Unused days are not credited.`,
      extra: "",
    };
  }

  const due = formatMinorAmount(preview.amount_due_minor, preview.currency);
  const credit = formatMinorAmount(preview.credit_minor, preview.currency);
  const renewal = formatMinorAmount(preview.next_renewal_amount_minor, preview.currency);
  const date = formatDate(preview.current_period_end);
  const stay = date
    ? `Your renewal date stays ${date} at ${renewal}.`
    : `Your renewal date is unchanged.`;
  const next = date
    ? `Next renewal ${date} at ${renewal}.`
    : `Next renewal at ${renewal}.`;

  let body;
  if (preview.amount_due_minor > 0) {
    body = `You'll be charged ${due} today. Unused time on your current plan is credited. ${stay}`;
  } else if (preview.credit_minor > 0) {
    body = `No charge today. ${credit} will apply to your next invoice. Your plan changes immediately. ${next}`;
  } else {
    body = `No charge today. Your plan changes immediately. ${next}`;
  }

  const extra =
    offer.legacy_tier < currentLegacyTier
      ? "Your used credits stay. If you've already used more than this plan includes, new AI replies pause until the next renewal."
      : "";
  return { body, extra };
}

export default function SwitchPlanDialog({
  open,
  offer,
  currentLegacyTier = null,
  preview = null,
  previewError = "",
  isPreviewLoading = false,
  isSubmitting = false,
  onConfirm,
  onCancel,
}) {
  if (offer?.amount_minor == null) return null;

  const confirmDisabled =
    isPreviewLoading || !preview || isSubmitting || Boolean(previewError);
  const copy = preview ? previewCopy(preview, offer, currentLegacyTier) : null;

  let description = "";
  let descriptionTestId;
  if (isPreviewLoading) {
    description = "Loading amount…";
    descriptionTestId = "billing-switch-preview-loading";
  } else if (previewError) {
    description = previewError;
  } else if (copy) {
    description = copy.body;
    descriptionTestId = "billing-switch-amount";
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent
        className="bg-[#0F1115] border-white/10 text-[#F3F4F6]"
        data-testid="billing-switch-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-lg text-[#F3F4F6]">
            Confirm switch
          </DialogTitle>
          <DialogDescription
            className="font-body text-sm text-[#9CA3AF]"
            data-testid={descriptionTestId}
          >
            {description}
          </DialogDescription>
          {copy?.extra ? (
            <p className="font-body text-sm text-[#9CA3AF]">{copy.extra}</p>
          ) : null}
          {preview && !isPreviewLoading ? (
            <span data-testid="billing-switch-settlement">{preview.settlement}</span>
          ) : null}
        </DialogHeader>
        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-4 py-2 font-heading font-bold text-sm bg-white/10 text-[#F3F4F6] hover:bg-white/15"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="rounded-lg px-4 py-2 font-heading font-bold text-sm bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90 disabled:opacity-50"
          >
            Confirm switch
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
